import { faArrowRight, faCalendar } from '@fortawesome/pro-light-svg-icons';
import { faCircleChevronLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faThumbsUp } from '@fortawesome/sharp-regular-svg-icons';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../shared/components/button/Button';
import { useHttpClient } from '../../shared/hooks/http-hook';
import HorizontalSlider from '../components/HorizontalSlider';
import Influencers from '../components/Influencers';
import Journal from '../components/Journal';
import NumberInput from '../components/NumberInput.tsx';
import RadioButton from '../components/RadioButton.tsx';
import TrackerHeading from '../components/TrackerHeading';

function SmokingTracker(props) {
  const { sendRequest } = useHttpClient();
  const { t } = useTranslation();
  const [smokeFree, setSmokeFree] = useState(false);
  const [hadSmoke, setHadSmoke] = useState(false);
  const [cigarettes, setCigarettes] = useState(0);
  const [isSaved, setIsSaved] = useState(false);
  const [date, setDate] = useState(new Date().toJSON().slice(0, 10));
  const [data, setData] = useState(null);
  const [journal, setJournal] = useState(null);
  const [currentTracker, setCurrentTracker] = useState(null);
  const [daysNotSmoked, setDaysNotSmoked] = useState(0);

  const daysSmokeFree = useRef(null);

  const getData = useCallback(
    async (requestedDate) => {
      if (requestedDate.length > 0) {
        try {
          const response = await sendRequest(
            `${process.env.REACT_APP_BACKEND_URL}/trackers/smoking/${requestedDate}`
          );
          if (response) {
            daysSmokeFree.current = response.daysNotSmoked;
            setDaysNotSmoked(response.daysNotSmoked);
          }
          if (response.tracker?._id) {
            const { tracker } = response;
            setCurrentTracker({
              id: tracker._id,
              trackerDate: tracker.tracker_date.toString().slice(0, 10),
            });
            const trackerData = JSON.parse(tracker.data);
            setData(trackerData);
            setJournal({
              enhancers: tracker.enhancers,
              inhibitors: tracker.inhibitors,
              journal: tracker.journal,
            });
            setIsSaved(true);

            if (trackerData.smoke_free) {
              setCigarettes(0);
              setHadSmoke(false);
              setSmokeFree(true);
            } else {
              setCigarettes(Number(trackerData.smoke_per_day));
              setHadSmoke(true);
              setSmokeFree(false);
            }
          } else {
            setCurrentTracker({
              id: '',
              trackerDate: requestedDate,
            });
            setData({
              smoke_free: '',
              craving_intensity: 5,
              bar_index: 16,
              smoke_per_day: 0,
            });
            setJournal({
              enhancers: [],
              inhibitors: [],
              journal: '',
            });
            setCigarettes(0);
            setHadSmoke(false);
            setSmokeFree(false);
          }
        } catch (err) {}
      }
    },
    [sendRequest]
  );

  useEffect(() => {
    getData(date);
  }, [date, getData]);

  const saveResponse = (location, stat) => {
    if (location === 'journal') {
      setJournal((prevState) => {
        return {
          ...prevState,
          [stat.id]: stat.value,
        };
      });
    } else {
      setData((prevState) => {
        return {
          ...prevState,
          [stat.id]: stat.value,
        };
      });
    }
  };

  const sendData = async () => {
    let method = 'POST';
    let url = `${process.env.REACT_APP_BACKEND_URL}/trackers/`;
    if (currentTracker.id !== '') {
      method = 'PUT';
      url = `${process.env.REACT_APP_BACKEND_URL}/trackers/${currentTracker.id}`;
    }
    try {
      const response = await sendRequest(
        url,
        method,
        JSON.stringify({
          trackerDate: date,
          type: 'smoking',
          data,
          hidden: false,
          enhancers: journal.enhancers,
          inhibitors: journal.inhibitors,
          journal: journal.journal,
          platform: 'web',
        })
      );
      if (response) {
        setIsSaved(true);
        props.closeTrackers();
      }
    } catch (err) {}
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    sendData();
  };

  return (
    <div
      id="snm-smoking"
      className="tracker-container"
      role="region"
      aria-label={`${t('T355')} ${t('tracker.region.title')}`}
    >
      <form className="tracker-form" onSubmit={handleSubmit} noValidate={false}>
        <div className="trackers-inner">
          <div className="back-to-main">
            <button
              className="back-btn"
              aria-label={t('A136')}
              title={t('A136')}
              onClick={props.handleBackToMain}
              type="button"
            >
              <FontAwesomeIcon icon={faCircleChevronLeft} />
            </button>
          </div>
          <TrackerHeading title="T100" setDate={setDate} isSaved={isSaved} initialDate={date} />
          <div className="trackers-body" role="group" aria-label={t('modal.tracker.details')}>
            <div className="tracker-sections">
              <div className="ind-tracker" role="group" aria-labelledby="craving-intensity-title">
                <h2 className="title" id="craving-intensity-title">
                  {t('T101')}
                </h2>
                <HorizontalSlider
                  scale1to10
                  questionid="craving_intensity"
                  data={data}
                  saveResponse={saveResponse}
                  title={t('T101')}
                />
              </div>
            </div>
            <fieldset className="ind-tracker no-padding  ind-tracker-fieldset fs-exclude">
              <legend className="title title-with-border" id="smoke-free-title">
                {t('T102')}
              </legend>
              <div className="buffer" />
              <div
                className="yes-no-btn fs-exclude"
                // role="radiogroup"
                // aria-labelledby="smoke-free-title"
              >
                <RadioButton
                  label={t('T066')}
                  name="smoke-status"
                  value="yes"
                  isRequired
                  checked={hadSmoke}
                  isSelected={hadSmoke}
                  onChange={() => {
                    setSmokeFree(false);
                    setHadSmoke(true);
                    setCigarettes(0);
                    saveResponse('data', {
                      id: 'smoke_free',
                      value: false,
                    });
                    saveResponse('journal', {
                      id: 'enhancers',
                      value: [],
                    });
                  }}
                />
                <RadioButton
                  label={t('T067')}
                  name="smoke-status"
                  value="no"
                  checked={smokeFree}
                  isSelected={smokeFree}
                  onChange={() => {
                    if (daysSmokeFree.current === daysNotSmoked && !smokeFree) {
                      setDaysNotSmoked(() => daysNotSmoked + 1);
                    }
                    setHadSmoke(false);
                    setSmokeFree(true);
                    setCigarettes(0);
                    saveResponse('data', {
                      id: 'smoke_per_day',
                      value: 0,
                    });
                    saveResponse('data', {
                      id: 'smoke_free',
                      value: true,
                    });
                    saveResponse('journal', {
                      id: 'inhibitors',
                      value: [],
                    });
                  }}
                />
              </div>
              <div className="buffer" />
            </fieldset>
            {smokeFree && (
              <>
                {daysNotSmoked && (
                  <div
                    className="whc-cont alc fs-exclude"
                    role="group"
                    aria-label={`${t('trackers.smoking.daysNotSmoked')}`}
                  >
                    <div className="whc-body">
                      <div className="whc-heading">
                        <FontAwesomeIcon icon={faThumbsUp} className="whc-thumbs-up" />
                        {daysNotSmoked === 1 ? (
                          t('T553')
                        ) : (
                          <>
                            {t('T108')}!{daysNotSmoked} {t('T105')}
                          </>
                        )}
                      </div>
                      <div className="whc-text">
                        {daysNotSmoked === 1 ? (
                          <>{t('T554')}</>
                        ) : (
                          <>
                            {t('T104')} {daysNotSmoked} {t('T105')} {t('T109')}
                          </>
                        )}
                      </div>
                    </div>
                    <div className="icon-cont" aria-hidden="true">
                      <FontAwesomeIcon icon={faCalendar} />
                      <div className="days">{daysNotSmoked}</div>
                    </div>
                  </div>
                )}
                <div
                  className="ind-tracker"
                  role="group"
                  aria-labelledby="not-smoking-influencers-title"
                >
                  <div className="tracker-content-heading">
                    <h3 id="not-smoking-influencers-title" className="title">
                      {t('T106')}
                    </h3>
                    <p className="optional-text">{t('T019')}</p>
                  </div>
                  <Influencers
                    notsmoking
                    questionid={['enhancers', 'inhibitors']}
                    data={journal}
                    saveResponse={saveResponse}
                    greenListAriaLabel={t('trackers.influencers.smokingPositiveInfluencers')}
                    titleId="not-smoking-influencers-title"
                  />
                  <div className="buffer" />
                </div>
              </>
            )}
            {hadSmoke && (
              <>
                <div className="ind-tracker" role="group" aria-labelledby="smoke-per-day-title">
                  <div className="number-input-section">
                    <h3 className="title" id="smoke-per-day-title">
                      {t('T103')}
                    </h3>
                    <NumberInput
                      inputName="smoke_per_day"
                      questionid="smoke_per_day"
                      startValue={cigarettes}
                      minValue={1}
                      maxValue={30}
                      step={1}
                      required
                      onSetValue={(value) => {
                        setCigarettes(value);
                        saveResponse('data', {
                          id: 'smoke_per_day',
                          value,
                        });
                      }}
                      controlsInfo={t('trackers.smoking.unit')}
                    />
                  </div>
                  <div className="buffer" />
                </div>
                <div
                  className="ind-tracker"
                  role="group"
                  aria-labelledby="smoking-influencers-title"
                >
                  <div className="tracker-content-heading">
                    <h3 id="smoking-influencers-title" className="title">
                      {t('T107')}
                    </h3>
                    <p className="optional-text">{t('T019')}</p>
                  </div>
                  <Influencers
                    smoking
                    questionid={['enhancers', 'inhibitors']}
                    data={journal}
                    saveResponse={saveResponse}
                    redListAriaLabel={t('trackers.influencers.smokingNegativeInfluencers')}
                    titleId="smoking-influencers-title"
                  />
                  <div className="buffer" />
                </div>
              </>
            )}

            <div className="ind-tracker" role="group" aria-label={`${t('T355')} - ${t('T018')}`}>
              <Journal
                placeholder={t('T547')}
                questionid="journal"
                data={journal}
                saveResponse={saveResponse}
              />
            </div>
          </div>
        </div>
        <div className="buttons">
          <Button onClick={props.closeTrackers} variant="secondary" color="dark">
            {t('T015')}
          </Button>
          <Button type="submit" icon={{ faIconDefinition: faArrowRight }} iconPosition="right">
            {t('T016')}
          </Button>
        </div>
      </form>
    </div>
  );
}
export default SmokingTracker;
